import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useMemo, useState } from "react";
import {
  externalService,
  ExternalMovie,
  ExternalCategory,
} from "@/services/externalService";
import { articleService } from "@/services/articleService";
import {
  phimApiService,
  PhimApiMovie,

} from "@/services/phimApiService";
import { ophimApiService } from "@/services/ophimApiService";
import { useDebounce } from "@/hooks/useDebounce";
import { showToast } from "@/lib/toast";
import {
  Search,
  RefreshCw,
  Film,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  Download,
} from "lucide-react";

interface ModalAddPostProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

// Pagination constants
const ITEMS_PER_PAGE = 50;
const PHIMAPI_ITEMS_PER_PAGE = 24;

// PhimAPI type options
const PHIMAPI_TYPES = [
  { value: "phim-bo", label: "Phim bộ" },
  { value: "phim-le", label: "Phim lẻ" },
  { value: "tv-shows", label: "TV Shows" },
  { value: "hoat-hinh", label: "Hoạt hình" },
  { value: "phim-vietsub", label: "Phim Vietsub" },
  { value: "phim-thuyet-minh", label: "Phim Thuyết Minh" },
  { value: "phim-long-tieng", label: "Phim Lồng Tiếng" },
];

const OPHIM_TYPES = [
  { value: "phim-moi", label: "Phim mới" },
  { value: "phim-bo", label: "Phim bộ" },
  { value: "phim-le", label: "Phim lẻ" },
  { value: "tv-shows", label: "TV Shows" },
  { value: "hoat-hinh", label: "Hoạt hình" },
  { value: "phim-vietsub", label: "Phim Vietsub" },
  { value: "phim-thuyet-minh", label: "Phim Thuyết Minh" },
  { value: "phim-long-tien", label: "Phim Lồng Tiếng" },
  { value: "phim-bo-dang-chieu", label: "Phim bộ đang chiếu" },
  { value: "phim-bo-hoan-thanh", label: "Phim bộ hoàn thành" },
  { value: "phim-sap-chieu", label: "Phim sắp chiếu" },
  { value: "subteam", label: "Subteam" },
  { value: "phim-chieu-rap", label: "Phim chiếu rạp" },
];

const OPHIM_ITEMS_PER_PAGE = 24;

export default function ModalAddPost({
  isOpen,
  onClose,
  onCompleted,
}: ModalAddPostProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<"external" | "phimapi" | "ophim">(
    "external"
  );

  // External Movie tab states
  const [movies, setMovies] = useState<ExternalMovie[]>([]);
  const [categories, setCategories] = useState<ExternalCategory[]>([]);
  const [selectedMovieIds, setSelectedMovieIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const debounced = useDebounce(search, 400);

  // PhimAPI tab states
  const [phimApiMovies, setPhimApiMovies] = useState<PhimApiMovie[]>([]);
  const [phimApiType, setPhimApiType] = useState<string>("phim-bo");
  const [phimApiSearch, setPhimApiSearch] = useState("");
  const [phimApiPage, setPhimApiPage] = useState(1);
  const [phimApiTotalPages, setPhimApiTotalPages] = useState(1);
  const [phimApiTotalItems, setPhimApiTotalItems] = useState(0);
  const [phimApiCdnDomain, setPhimApiCdnDomain] = useState<string>(
    "https://phimimg.com"
  );
  const [selectedPhimApiSlugs, setSelectedPhimApiSlugs] = useState<string[]>(
    []
  );
  const [isLoadingPhimApi, setIsLoadingPhimApi] = useState(false);
  const [isSubmittingPhimApi, setIsSubmittingPhimApi] = useState(false);
  const debouncedPhimApi = useDebounce(phimApiSearch, 400);

  // OPhim tab states
  const [ophimMovies, setOphimMovies] = useState<PhimApiMovie[]>([]);
  const [ophimType, setOphimType] = useState<string>("phim-moi");
  const [ophimSearch, setOphimSearch] = useState("");
  const [ophimPage, setOphimPage] = useState(1);
  const [ophimTotalPages, setOphimTotalPages] = useState(1);
  const [ophimTotalItems, setOphimTotalItems] = useState(0);
  const [ophimCdnDomain, setOphimCdnDomain] = useState<string>(
    "https://img.ophim.live/uploads/movies"
  );
  const [selectedOphimSlugs, setSelectedOphimSlugs] = useState<string[]>([]);
  const [isLoadingOphim, setIsLoadingOphim] = useState(false);
  const [isSubmittingOphim, setIsSubmittingOphim] = useState(false);
  const debouncedOphim = useDebounce(ophimSearch, 400);

  // Load External movies and categories
  useEffect(() => {
    if (!isOpen || activeTab !== "external") return;
    const run = async () => {
      setIsLoading(true);
      try {
        const [moviesRes, categoriesRes]: any[] = await Promise.all([
          externalService.getAllMovies(),
          externalService.getAllCategories(),
        ]);

        let moviesList: ExternalMovie[] = [];
        if (moviesRes) {
          if (Array.isArray(moviesRes)) {
            moviesList = moviesRes;
          } else if (moviesRes.data) {
            if (Array.isArray(moviesRes.data)) {
              moviesList = moviesRes.data;
            } else if (
              moviesRes.data.data &&
              Array.isArray(moviesRes.data.data)
            ) {
              moviesList = moviesRes.data.data;
            } else if (
              moviesRes.data.data &&
              moviesRes.data.data.data &&
              Array.isArray(moviesRes.data.data.data)
            ) {
              moviesList = moviesRes.data.data.data;
            }
          }
        }
        setMovies(moviesList);

        let categoriesList: ExternalCategory[] = [];
        if (categoriesRes) {
          if (Array.isArray(categoriesRes)) {
            categoriesList = categoriesRes;
          } else if (categoriesRes.data) {
            if (Array.isArray(categoriesRes.data)) {
              categoriesList = categoriesRes.data;
            } else if (
              categoriesRes.data.data &&
              Array.isArray(categoriesRes.data.data)
            ) {
              categoriesList = categoriesRes.data.data;
            } else if (
              categoriesRes.data.data &&
              categoriesRes.data.data.data &&
              Array.isArray(categoriesRes.data.data.data)
            ) {
              categoriesList = categoriesRes.data.data.data;
            }
          }
        }
        setCategories(categoriesList);

        if (moviesList.length === 0) {
          showToast.error("Lỗi", "Không thể tải danh sách phim");
        }
      } catch (error) {
        console.error("Error loading data:", error);
        showToast.error("Lỗi", "Có lỗi xảy ra khi tải dữ liệu");
        setMovies([]);
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [isOpen, activeTab]);

  // Load PhimAPI movies (list or search)
  useEffect(() => {
    if (!isOpen || activeTab !== "phimapi") return;
    const loadPhimApiMovies = async () => {
      setIsLoadingPhimApi(true);
      try {
        let res: any;
        if (debouncedPhimApi) {
          // Server-side search
          res = await phimApiService.searchMovies({
            keyword: debouncedPhimApi,
            page: phimApiPage,
            limit: PHIMAPI_ITEMS_PER_PAGE,
          });
        } else {
          // Browse by type
          res = await phimApiService.getMovieList(phimApiType, {
            page: phimApiPage,
            limit: PHIMAPI_ITEMS_PER_PAGE,
          });
        }

        const parseResponse = (data: any) => {
          setPhimApiMovies(data.items || []);
          const pagination = data.pagination || data.params?.pagination;
          const totalItems = pagination?.totalItems || 0;
          const perPage = pagination?.totalItemsPerPage || PHIMAPI_ITEMS_PER_PAGE;
          setPhimApiTotalPages(totalItems > 0 ? Math.ceil(totalItems / perPage) : (pagination?.totalPages || 1));
          setPhimApiTotalItems(totalItems);
          if (data.APP_DOMAIN_CDN_IMAGE) {
            setPhimApiCdnDomain(data.APP_DOMAIN_CDN_IMAGE);
          }
        };

        if (res?.ok && res?.data?.data?.data) {
          parseResponse(res.data.data.data);
        } else if (res?.data?.data?.items) {
          parseResponse(res.data.data);
        } else {
          setPhimApiMovies([]);
          setPhimApiTotalPages(1);
          setPhimApiTotalItems(0);
        }
      } catch (error) {
        console.error("Error loading PhimAPI movies:", error);
        showToast.error("Lỗi", "Không thể tải danh sách phim từ PhimAPI");
        setPhimApiMovies([]);
      } finally {
        setIsLoadingPhimApi(false);
      }
    };
    loadPhimApiMovies();
  }, [isOpen, activeTab, phimApiType, phimApiPage, debouncedPhimApi]);

  // Reset PhimAPI page when type or search changes
  useEffect(() => {
    setPhimApiPage(1);
  }, [phimApiType, debouncedPhimApi]);

  // Load OPhim movies (list or search)
  useEffect(() => {
    if (!isOpen || activeTab !== "ophim") return;
    const loadOphimMovies = async () => {
      setIsLoadingOphim(true);
      try {
        let res: any;
        if (debouncedOphim) {
          // Server-side search
          res = await ophimApiService.searchMovies({
            keyword: debouncedOphim,
            page: ophimPage,
            limit: OPHIM_ITEMS_PER_PAGE,
          });
        } else {
          // Browse by type
          res = await ophimApiService.getMovieList(ophimType, {
            page: ophimPage,
            limit: OPHIM_ITEMS_PER_PAGE,
          });
        }

        const parseResponse = (data: any) => {
          setOphimMovies(data.items || []);
          const pagination = data.pagination || data.params?.pagination;
          const totalItems = pagination?.totalItems || 0;
          const perPage = pagination?.totalItemsPerPage || OPHIM_ITEMS_PER_PAGE;
          setOphimTotalPages(totalItems > 0 ? Math.ceil(totalItems / perPage) : (pagination?.totalPages || 1));
          setOphimTotalItems(totalItems);
          if (data.APP_DOMAIN_CDN_IMAGE) {
            setOphimCdnDomain(data.APP_DOMAIN_CDN_IMAGE);
          }
        };

        if (res?.ok && res?.data?.data?.data) {
          parseResponse(res.data.data.data);
        } else if (res?.data?.data?.items) {
          parseResponse(res.data.data);
        } else {
          setOphimMovies([]);
          setOphimTotalPages(1);
          setOphimTotalItems(0);
        }
      } catch (error) {
        console.error("Error loading OPhim movies:", error);
        showToast.error("Lỗi", "Không thể tải danh sách phim từ OPhim");
        setOphimMovies([]);
      } finally {
        setIsLoadingOphim(false);
      }
    };
    loadOphimMovies();
  }, [isOpen, activeTab, ophimType, ophimPage, debouncedOphim]);

  // Reset OPhim page when type or search changes
  useEffect(() => {
    setOphimPage(1);
  }, [ophimType, debouncedOphim]);

  // External movies filter
  const filtered = useMemo(() => {
    let filteredMovies = movies;

    if (selectedCategoryId && selectedCategoryId !== "all") {
      filteredMovies = filteredMovies.filter((movie) => {
        if (Array.isArray(movie.category)) {
          return movie.category.some(
            (cat: any) =>
              (typeof cat === "string" ? cat : (cat as any)?._id) ===
              selectedCategoryId
          );
        } else if (movie.category) {
          const catId =
            typeof movie.category === "string"
              ? movie.category
              : (movie.category as any)?._id;
          return catId === selectedCategoryId;
        }
        return false;
      });
    }

    if (debounced) {
      const s = debounced.toLowerCase();
      filteredMovies = filteredMovies.filter(
        (m) =>
          (m.name || "").toLowerCase().includes(s) ||
          String(m.year || "").includes(s)
      );
    }

    return filteredMovies;
  }, [movies, debounced, selectedCategoryId]);

  // PhimAPI & OPhim now use server-side search, no client-side filter needed
  const filteredPhimApiMovies = phimApiMovies;
  const filteredOphimMovies = ophimMovies;

  // External pagination
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedMovies = filtered.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategoryId, debounced]);

  // Handle External submit
  const handleSubmit = async () => {
    if (selectedMovieIds.length === 0) {
      showToast.error("Vui lòng chọn ít nhất 1 phim");
      return;
    }
    setIsSubmitting(true);
    try {
      const res: any = await articleService.createBulk({
        movie_ids: selectedMovieIds,
      });

      if (res && (res.ok || res.data || res.status === 200)) {
        showToast.success(
          "Tạo bài viết thành công",
          `Đã tạo ${selectedMovieIds.length} bài viết`
        );
        onCompleted?.();
        onClose();
        setSelectedMovieIds([]);
        setSelectedCategoryId("all");
        setSearch("");
        setCurrentPage(1);
      } else {
        showToast.error(
          "Tạo bài viết thất bại",
          res?.message || "Có lỗi xảy ra"
        );
      }
    } catch (error) {
      showToast.error("Lỗi", "Có lỗi xảy ra khi tạo bài viết");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle PhimAPI submit
  const handlePhimApiSubmit = async () => {
    if (selectedPhimApiSlugs.length === 0) {
      showToast.error("Vui lòng chọn ít nhất 1 phim");
      return;
    }
    setIsSubmittingPhimApi(true);
    try {
      const res: any =
        await phimApiService.createBulkFromPhimApi(selectedPhimApiSlugs);

      if (res && (res.ok || res.data)) {
        const data = res.data?.data?.data || res.data?.data || res.data;
        const successCount = data?.success?.length || 0;
        const skippedCount = data?.skipped?.length || 0;
        const failedCount = data?.failed?.length || 0;

        if (successCount > 0) {
          showToast.success(
            "Import thành công",
            `Đã tạo ${successCount} bài viết${skippedCount > 0 ? `, bỏ qua ${skippedCount}` : ""}${failedCount > 0 ? `, lỗi ${failedCount}` : ""}`
          );
        } else if (skippedCount > 0) {
          showToast.warning(
            "Bỏ qua tất cả",
            `${skippedCount} phim đã tồn tại trong hệ thống`
          );
        } else {
          showToast.error("Import thất bại", `${failedCount} phim bị lỗi`);
        }

        onCompleted?.();
        onClose();
        setSelectedPhimApiSlugs([]);
        setPhimApiPage(1);
        setPhimApiSearch("");
      } else {
        showToast.error("Import thất bại", res?.message || "Có lỗi xảy ra");
      }
    } catch (error) {
      console.error("Error importing from PhimAPI:", error);
      showToast.error("Lỗi", "Có lỗi xảy ra khi import phim");
    } finally {
      setIsSubmittingPhimApi(false);
    }
  };

  // Handle OPhim submit
  const handleOphimSubmit = async () => {
    if (selectedOphimSlugs.length === 0) {
      showToast.error("Vui lòng chọn ít nhất 1 phim");
      return;
    }
    setIsSubmittingOphim(true);
    try {
      const res: any =
        await ophimApiService.createBulkFromOPhim(selectedOphimSlugs);

      if (res && (res.ok || res.data)) {
        const data = res.data?.data?.data || res.data?.data || res.data;
        const successCount = data?.success?.length || 0;
        const skippedCount = data?.skipped?.length || 0;
        const failedCount = data?.failed?.length || 0;

        if (successCount > 0) {
          showToast.success(
            "Import thành công",
            `Đã tạo ${successCount} bài viết${skippedCount > 0 ? `, bỏ qua ${skippedCount}` : ""}${failedCount > 0 ? `, lỗi ${failedCount}` : ""}`
          );
        } else if (skippedCount > 0) {
          showToast.warning(
            "Bỏ qua tất cả",
            `${skippedCount} phim đã tồn tại trong hệ thống`
          );
        } else {
          showToast.error("Import thất bại", `${failedCount} phim bị lỗi`);
        }

        onCompleted?.();
        onClose();
        setSelectedOphimSlugs([]);
        setOphimPage(1);
        setOphimSearch("");
      } else {
        showToast.error("Import thất bại", res?.message || "Có lỗi xảy ra");
      }
    } catch (error) {
      console.error("Error importing from OPhim:", error);
      showToast.error("Lỗi", "Có lỗi xảy ra khi import phim từ OPhim");
    } finally {
      setIsSubmittingOphim(false);
    }
  };

  // Reset states when closing
  const handleClose = () => {
    setSelectedMovieIds([]);
    setSelectedPhimApiSlugs([]);
    setSelectedOphimSlugs([]);
    setSearch("");
    setPhimApiSearch("");
    setOphimSearch("");
    setCurrentPage(1);
    setPhimApiPage(1);
    setOphimPage(1);
    setSelectedCategoryId("all");
    setPhimApiType("phim-bo");
    setOphimType("phim-moi");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 flex-1 min-h-0 flex flex-col">
          <DialogHeader>
            <DialogTitle>Thêm mới bài viết</DialogTitle>
          </DialogHeader>

          <Tabs
            value={activeTab}
            onValueChange={(v) =>
              setActiveTab(v as "external" | "phimapi" | "ophim")
            }
            className="mt-4 flex-1 flex flex-col min-h-0"
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="external">Phim nội bộ</TabsTrigger>
              <TabsTrigger value="phimapi">Import từ PhimAPI</TabsTrigger>
              <TabsTrigger value="ophim">Import từ OPhim</TabsTrigger>
            </TabsList>

            {/* External Movie Tab */}
            <TabsContent
              value="external"
              className="flex-1 flex flex-col min-h-0 mt-4"
            >
              {/* Filters */}
              <div className="flex gap-3 mb-4">
                <div className="flex-1">
                  <Select
                    value={selectedCategoryId}
                    onValueChange={setSelectedCategoryId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn thể loại phim" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto scrollbar-gentle">
                      <SelectItem value="all">Tất cả thể loại</SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category._id} value={category._id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Tìm kiếm phim theo tên hoặc năm..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {(selectedCategoryId !== "all" || search) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedCategoryId("all");
                      setSearch("");
                    }}
                    className="px-3"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* List */}
              <div className="flex-1 min-h-0 flex flex-col">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-500">
                      Đang tải danh sách phim...
                    </span>
                  </div>
                ) : movies.length === 0 ? (
                  <div className="flex items-center justify-center py-8 flex-1">
                    <div className="text-center">
                      <Film className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        Không có dữ liệu phim
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {filtered.length === 0 ? (
                      <div className="flex items-center justify-center py-8 flex-1">
                        <div className="text-center">
                          <Film className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm text-gray-500">
                            {selectedCategoryId !== "all" || search
                              ? "Không có phim nào phù hợp với bộ lọc"
                              : "Không có phim nào"}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 min-h-0">
                          <div className="h-full overflow-y-auto max-h-[400px] scrollbar-gentle">
                            <div className="space-y-2 p-2">
                              {paginatedMovies.map((movie: any) => {
                                const isSelected = selectedMovieIds.includes(
                                  movie._id
                                );
                                return (
                                  <div
                                    key={movie._id}
                                    className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                                      isSelected
                                        ? "border-blue-500 bg-blue-50"
                                        : "border-gray-200 hover:border-gray-300"
                                    }`}
                                    onClick={() => {
                                      setSelectedMovieIds((prev) =>
                                        prev.includes(movie._id)
                                          ? prev.filter(
                                              (id) => id !== movie._id
                                            )
                                          : [...prev, movie._id]
                                      );
                                    }}
                                  >
                                    <div className="flex gap-3 items-center">
                                      {movie?.posters?.[0] ? (
                                        <img
                                          src={`https://api.ophim69.com/api/v1/${movie.posters[0]}`}
                                          alt={movie.name}
                                          className="w-10 h-14 bg-gray-100 rounded object-cover"
                                          onError={(e) => {
                                            (
                                              e.target as HTMLImageElement
                                            ).src = "/placeholder-movie.jpg";
                                          }}
                                        />
                                      ) : (
                                        <div className="w-10 h-14 bg-gray-100 rounded" />
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <h4 className="text-sm font-medium text-gray-900 truncate">
                                          {movie.name}
                                        </h4>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {movie.year && (
                                            <span className="text-xs text-gray-500">
                                              Năm: {movie.year}
                                            </span>
                                          )}
                                          {movie.category && (
                                            <span className="text-xs text-blue-600">
                                              {" "}
                                              {Array.isArray(movie.category)
                                                ? movie.category
                                                    .map((cat: any) =>
                                                      typeof cat === "string"
                                                        ? cat
                                                        : cat.name
                                                    )
                                                    .join(", ")
                                                : typeof movie.category ===
                                                    "string"
                                                  ? movie.category
                                                  : movie.category.name}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {totalPages > 1 && (
                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                            <div className="text-sm text-gray-500">
                              Hiển thị {startIndex + 1}-
                              {Math.min(endIndex, filtered.length)} /{" "}
                              {filtered.length}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setCurrentPage((prev) =>
                                    Math.max(1, prev - 1)
                                  )
                                }
                                disabled={currentPage === 1}
                                className="px-3"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </Button>
                              <span className="text-sm text-gray-600 px-3">
                                {currentPage} / {totalPages}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setCurrentPage((prev) =>
                                    Math.min(totalPages, prev + 1)
                                  )
                                }
                                disabled={currentPage === totalPages}
                                className="px-3"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>

              <DialogFooter className="flex justify-between mt-4 items-center">
                <div className="text-sm text-gray-500">
                  Đã chọn: {selectedMovieIds.length} phim
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Hủy
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || selectedMovieIds.length === 0}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Thêm {selectedMovieIds.length} phim
                  </Button>
                </div>
              </DialogFooter>
            </TabsContent>

            {/* PhimAPI Tab */}
            <TabsContent
              value="phimapi"
              className="flex-1 flex flex-col min-h-0 mt-4"
            >
              {/* Filters */}
              <div className="flex gap-3 mb-4">
                <div className="flex-1">
                  <Select value={phimApiType} onValueChange={setPhimApiType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn loại phim" />
                    </SelectTrigger>
                    <SelectContent>
                      {PHIMAPI_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Lọc theo tên phim..."
                    value={phimApiSearch}
                    onChange={(e) => setPhimApiSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {phimApiSearch && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPhimApiSearch("")}
                    className="px-3"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* List */}
              <div className="flex-1 min-h-0 flex flex-col">
                {isLoadingPhimApi ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-500">
                      Đang tải danh sách phim từ PhimAPI...
                    </span>
                  </div>
                ) : filteredPhimApiMovies.length === 0 ? (
                  <div className="flex items-center justify-center py-8 flex-1">
                    <div className="text-center">
                      <Film className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        {phimApiSearch
                          ? "Không tìm thấy phim phù hợp"
                          : "Không có phim nào"}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-h-0">
                      <div className="h-full overflow-y-auto max-h-[400px] scrollbar-gentle">
                        <div className="space-y-2 p-2">
                          {filteredPhimApiMovies.map((movie) => {
                            const isSelected = selectedPhimApiSlugs.includes(
                              movie.slug
                            );
                            return (
                              <div
                                key={movie._id}
                                className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                                  isSelected
                                    ? "border-green-500 bg-green-50"
                                    : "border-gray-200 hover:border-gray-300"
                                }`}
                                onClick={() => {
                                  setSelectedPhimApiSlugs((prev) =>
                                    prev.includes(movie.slug)
                                      ? prev.filter((s) => s !== movie.slug)
                                      : [...prev, movie.slug]
                                  );
                                }}
                              >
                                <div className="flex gap-3 items-center">
                                  {movie.poster_url || movie.thumb_url ? (
                                    <img
                                      src={`${phimApiCdnDomain}/${movie.poster_url || movie.thumb_url}`}
                                      alt={movie.name}
                                      className="w-10 h-14 bg-gray-100 rounded object-cover"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src =
                                          "/placeholder-movie.jpg";
                                      }}
                                    />
                                  ) : (
                                    <div className="w-10 h-14 bg-gray-100 rounded flex items-center justify-center">
                                      <Film className="w-5 h-5 text-gray-400" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium text-gray-900 truncate">
                                      {movie.name}
                                    </h4>
                                    {movie.origin_name && (
                                      <p className="text-xs text-gray-500 truncate">
                                        {movie.origin_name}
                                      </p>
                                    )}
                                    <div className="flex flex-wrap gap-2 mt-1">
                                      {movie.year && (
                                        <span className="text-xs text-gray-500">
                                          {movie.year}
                                        </span>
                                      )}
                                      {movie.episode_current && (
                                        <span className="text-xs text-blue-600">
                                          {movie.episode_current}
                                        </span>
                                      )}
                                      {movie.quality && (
                                        <span className="text-xs bg-yellow-100 text-yellow-700 px-1 rounded">
                                          {movie.quality}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                      <div className="text-sm text-gray-500">
                        Trang {phimApiPage} / {phimApiTotalPages} (
                        {phimApiTotalItems} phim)
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setPhimApiPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={phimApiPage === 1 || isLoadingPhimApi}
                          className="px-3"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-gray-600 px-3">
                          {phimApiPage} / {phimApiTotalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setPhimApiPage((prev) =>
                              Math.min(phimApiTotalPages, prev + 1)
                            )
                          }
                          disabled={
                            phimApiPage === phimApiTotalPages ||
                            isLoadingPhimApi
                          }
                          className="px-3"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <DialogFooter className="flex justify-between mt-4 items-center">
                <div className="text-sm text-gray-500">
                  Đã chọn: {selectedPhimApiSlugs.length} phim
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSubmittingPhimApi}
                  >
                    Hủy
                  </Button>
                  <Button
                    onClick={handlePhimApiSubmit}
                    disabled={
                      isSubmittingPhimApi || selectedPhimApiSlugs.length === 0
                    }
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Import {selectedPhimApiSlugs.length} phim
                  </Button>
                </div>
              </DialogFooter>
            </TabsContent>

            {/* OPhim Tab */}
            <TabsContent
              value="ophim"
              className="flex-1 flex flex-col min-h-0 mt-4"
            >
              {/* Filters */}
              <div className="flex gap-3 mb-4">
                <div className="flex-1">
                  <Select value={ophimType} onValueChange={setOphimType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn loại phim" />
                    </SelectTrigger>
                    <SelectContent>
                      {OPHIM_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Lọc theo tên phim..."
                    value={ophimSearch}
                    onChange={(e) => setOphimSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {ophimSearch && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOphimSearch("")}
                    className="px-3"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* List */}
              <div className="flex-1 min-h-0 flex flex-col">
                {isLoadingOphim ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
                    <span className="ml-2 text-gray-500">
                      Đang tải danh sách phim từ OPhim...
                    </span>
                  </div>
                ) : filteredOphimMovies.length === 0 ? (
                  <div className="flex items-center justify-center py-8 flex-1">
                    <div className="text-center">
                      <Film className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        {ophimSearch
                          ? "Không tìm thấy phim phù hợp"
                          : "Không có phim nào"}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-h-0">
                      <div className="h-full overflow-y-auto max-h-[400px] scrollbar-gentle">
                        <div className="space-y-2 p-2">
                          {filteredOphimMovies.map((movie) => {
                            const isSelected = selectedOphimSlugs.includes(
                              movie.slug
                            );
                            return (
                              <div
                                key={movie._id}
                                className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                                  isSelected
                                    ? "border-purple-500 bg-purple-50"
                                    : "border-gray-200 hover:border-gray-300"
                                }`}
                                onClick={() => {
                                  setSelectedOphimSlugs((prev) =>
                                    prev.includes(movie.slug)
                                      ? prev.filter((s) => s !== movie.slug)
                                      : [...prev, movie.slug]
                                  );
                                }}
                              >
                                <div className="flex gap-3 items-center">
                                  {movie.poster_url || movie.thumb_url ? (
                                    <img
                                      src={`${ophimCdnDomain}/uploads/movies/${movie.poster_url || movie.thumb_url}`}
                                      alt={movie.name}
                                      className="w-10 h-14 bg-gray-100 rounded object-cover"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src =
                                          "/placeholder-movie.jpg";
                                      }}
                                    />
                                  ) : (
                                    <div className="w-10 h-14 bg-gray-100 rounded flex items-center justify-center">
                                      <Film className="w-5 h-5 text-gray-400" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium text-gray-900 truncate">
                                      {movie.name}
                                    </h4>
                                    {movie.origin_name && (
                                      <p className="text-xs text-gray-500 truncate">
                                        {movie.origin_name}
                                      </p>
                                    )}
                                    <div className="flex flex-wrap gap-2 mt-1">
                                      {movie.year && (
                                        <span className="text-xs text-gray-500">
                                          {movie.year}
                                        </span>
                                      )}
                                      {movie.episode_current && (
                                        <span className="text-xs text-purple-600">
                                          {movie.episode_current}
                                        </span>
                                      )}
                                      {movie.quality && (
                                        <span className="text-xs bg-yellow-100 text-yellow-700 px-1 rounded">
                                          {movie.quality}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                      <div className="text-sm text-gray-500">
                        Trang {ophimPage} / {ophimTotalPages} ({ophimTotalItems}{" "}
                        phim)
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setOphimPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={ophimPage === 1 || isLoadingOphim}
                          className="px-3"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-gray-600 px-3">
                          {ophimPage} / {ophimTotalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setOphimPage((prev) =>
                              Math.min(ophimTotalPages, prev + 1)
                            )
                          }
                          disabled={
                            ophimPage === ophimTotalPages || isLoadingOphim
                          }
                          className="px-3"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <DialogFooter className="flex justify-between mt-4 items-center">
                <div className="text-sm text-gray-500">
                  Đã chọn: {selectedOphimSlugs.length} phim
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSubmittingOphim}
                  >
                    Hủy
                  </Button>
                  <Button
                    onClick={handleOphimSubmit}
                    disabled={
                      isSubmittingOphim || selectedOphimSlugs.length === 0
                    }
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Import {selectedOphimSlugs.length} phim
                  </Button>
                </div>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
